<template>
  <div class="dashboard">
    <!-- 顶部标题栏 -->
    <header class="header">
      <div class="header-left">
        <h1 class="title">服务器监控大屏</h1>
        <span class="subtitle">Server Monitoring Dashboard</span>
      </div>
      <div class="header-right">
        <div class="stat-item">
          <span class="stat-label">服务器总数</span>
          <span class="stat-value total">{{ servers.length }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">在线</span>
          <span class="stat-value online">{{ onlineCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">离线</span>
          <span class="stat-value offline">{{ offlineCount }}</span>
        </div>
        <div class="header-time">
          <div class="time-clock">{{ currentTime }}</div>
          <div class="time-date">{{ currentDate }}</div>
        </div>
      </div>
    </header>

    <!-- 工具栏 -->
    <div class="toolbar" v-if="servers.length > 0">
      <input
        v-model="searchText"
        type="text"
        placeholder="搜索服务器名称或IP..."
        class="search-input"
      />
      <div class="column-switcher">
        <span class="col-label">列数:</span>
        <button v-for="n in [1,2,3,4,6]" :key="n"
          class="col-btn" :class="{ active: columnCount === n }"
          @click="setColumns(n)">{{ n }}</button>
        <button class="col-btn" :class="{ active: columnCount === 0 }"
          @click="setColumns(0)" title="自动">自动</button>
      </div>
      <span class="search-count" v-if="searchText">
        显示 {{ filteredServers.length }} / {{ servers.length }}
      </span>
    </div>

    <!-- 服务器网格 -->
    <div class="server-grid" :style="gridStyle">
      <!-- 空状态 -->
      <div v-if="servers.length === 0" class="empty-state">
        <div class="empty-icon">📡</div>
        <p>等待服务器上报数据...</p>
        <p class="empty-hint">请确保已在Windows服务器上运行监控Agent</p>
      </div>

      <!-- 服务器卡片 -->
      <div
        v-for="(server, index) in filteredServers"
        :key="server.hostname"
        class="server-card"
        :class="{ offline: server.status === 'offline', dragging: dragIndex === index, 'drag-over': dragOverIndex === index }"
        draggable="true"
        @dragstart="onDragStart($event, index)"
        @dragover.prevent="onDragOver($event, index)"
        @dragleave="onDragLeave"
        @drop="onDrop($event, index)"
        @dragend="onDragEnd"
      >
        <!-- 卡片头部：主机名和状态 -->
        <div class="card-header">
          <div class="server-info">
            <div class="server-name-row">
              <template v-if="editingHostname === server.hostname">
                <input
                  v-model="editingName"
                  class="rename-input"
                  @keyup.enter="saveRename(server.hostname)"
                  @keyup.escape="cancelRename"
                  @blur="saveRename(server.hostname)"
                  ref="renameInput"
                />
              </template>
              <template v-else>
                <div class="server-name" :title="'拖拽排序 | 点击编辑名称\n' + server.hostname">
                  {{ server.display_name }}
                </div>
                <button class="edit-btn" @click.stop="startRename(server)" title="重命名">&#9998;</button>
              </template>
            </div>
            <div class="server-hostname">{{ server.hostname }}</div>
            <div class="server-ip">{{ server.ip }}</div>
          </div>
          <div class="card-actions">
            <div class="status-badge" :class="server.status">
              <span class="status-dot"></span>
              {{ server.status === 'online' ? '在线' : '离线' }}
            </div>
            <button class="delete-btn" @click.stop="deleteServer(server)" title="删除此服务器">&#10005;</button>
          </div>
        </div>

        <!-- 卡片主体 -->
        <div class="card-body">
          <!-- CPU -->
          <div class="metric-row">
            <span class="metric-label">CPU</span>
            <div class="progress-bar">
              <div class="progress-fill cpu" :style="{ width: server.cpu_percent + '%' }"></div>
            </div>
            <span class="metric-value" :class="getUsageClass(server.cpu_percent)">{{ server.cpu_percent }}%</span>
          </div>
          <!-- 内存 -->
          <div class="metric-row">
            <span class="metric-label">内存</span>
            <div class="progress-bar">
              <div class="progress-fill memory" :style="{ width: server.memory_percent + '%' }"></div>
            </div>
            <span class="metric-value" :class="getUsageClass(server.memory_percent)">{{ server.memory_percent }}% ({{ server.memory_used_gb }}/{{ server.memory_total_gb }}GB)</span>
          </div>
          <!-- 磁盘 -->
          <div class="metric-row">
            <span class="metric-label">磁盘C:</span>
            <div class="progress-bar">
              <div class="progress-fill disk" :style="{ width: server.disk_percent + '%' }"></div>
            </div>
            <span class="metric-value" :class="getUsageClass(server.disk_percent)">{{ server.disk_percent }}% ({{ server.disk_used_gb }}/{{ server.disk_total_gb }}GB)</span>
          </div>
          <!-- 网络流量 -->
          <div class="network-section">
            <div class="section-title">实时网络流量</div>
            <div class="network-row">
              <div class="network-item upload"><span class="arrow">▲</span> 上传 {{ server.network_sent_mbps }} Mbps</div>
              <div class="network-item download"><span class="arrow">▼</span> 下载 {{ server.network_recv_mbps }} Mbps</div>
            </div>
            <div class="network-total">
              <span>累计上传: {{ server.network_sent_total_gb }} GB</span>
              <span>累计下载: {{ server.network_recv_total_gb }} GB</span>
            </div>
          </div>
          <!-- 底部信息 -->
          <div class="card-footer">
            <div class="footer-item" v-if="server.os_version"><span class="footer-label">系统:</span> {{ server.os_version }}</div>
            <div class="footer-item" v-if="server.boot_time"><span class="footer-label">启动:</span> {{ server.boot_time }}</div>
            <div class="footer-item"><span class="footer-label">上报:</span> {{ server.report_time }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部刷新信息 -->
    <div class="refresh-bar">
      <span class="refresh-dot"></span>
      自动刷新中 · 每3秒 · 上次刷新: {{ lastRefresh }}
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const STORAGE_KEY_COLS = 'dashboard_columns'
const STORAGE_KEY_ORDER = 'dashboard_order'

export default {
  name: 'HomeView',
  data() {
    return {
      servers: [],
      searchText: '',
      editingHostname: null,
      editingName: '',
      lastRefresh: '',
      timer: null,
      clockTimer: null,
      currentTime: '',
      currentDate: '',
      columnCount: parseInt(localStorage.getItem(STORAGE_KEY_COLS)) || 0,
      serverOrder: JSON.parse(localStorage.getItem(STORAGE_KEY_ORDER) || '[]'),
      dragIndex: null,
      dragOverIndex: null,
    }
  },
  computed: {
    onlineCount() {
      return this.servers.filter(s => s.status === 'online').length
    },
    offlineCount() {
      return this.servers.filter(s => s.status === 'offline').length
    },
    gridStyle() {
      if (this.columnCount === 0) return {}
      return { gridTemplateColumns: `repeat(${this.columnCount}, 1fr)` }
    },
    filteredServers() {
      let list = [...this.servers]
      // Apply saved order
      if (this.serverOrder.length > 0) {
        const orderMap = {}
        this.serverOrder.forEach((h, i) => { orderMap[h] = i })
        list.sort((a, b) => {
          const ai = orderMap[a.hostname] ?? 9999
          const bi = orderMap[b.hostname] ?? 9999
          return ai - bi
        })
      }
      // Apply search filter
      if (this.searchText) {
        const keyword = this.searchText.toLowerCase()
        list = list.filter(s =>
          s.display_name.toLowerCase().includes(keyword) ||
          s.hostname.toLowerCase().includes(keyword) ||
          s.ip.includes(keyword)
        )
      }
      return list
    },
  },
  created() {
    this.fetchData()
    this.timer = setInterval(this.fetchData, 3000)
    this.updateClock()
    this.clockTimer = setInterval(this.updateClock, 1000)
  },
  beforeUnmount() {
    clearInterval(this.timer)
    clearInterval(this.clockTimer)
  },
  methods: {
    async fetchData() {
      try {
        const res = await axios.get('/api/servers')
        if (res.data.code === 0) {
          this.servers = res.data.data
        }
        const now = new Date()
        this.lastRefresh = now.toLocaleTimeString('zh-CN', { hour12: false })
      } catch (e) {
        console.error('获取数据失败:', e)
      }
    },
    updateClock() {
      const now = new Date()
      this.currentTime = now.toLocaleTimeString('zh-CN', { hour12: false })
      this.currentDate = now.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short' })
    },
    getUsageClass(percent) {
      if (percent >= 90) return 'danger'
      if (percent >= 70) return 'warning'
      return ''
    },
    setColumns(n) {
      this.columnCount = n
      localStorage.setItem(STORAGE_KEY_COLS, n)
    },
    // Drag and drop
    onDragStart(e, index) {
      this.dragIndex = index
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', index)
    },
    onDragOver(e, index) {
      this.dragOverIndex = index
    },
    onDragLeave() {
      this.dragOverIndex = null
    },
    onDrop(e, dropIndex) {
      const fromIndex = parseInt(e.dataTransfer.getData('text/plain'))
      if (fromIndex === dropIndex) return
      // Get the current ordered list of hostnames
      const hosts = this.filteredServers.map(s => s.hostname)
      const [moved] = hosts.splice(fromIndex, 1)
      hosts.splice(dropIndex, 0, moved)
      this.serverOrder = hosts
      localStorage.setItem(STORAGE_KEY_ORDER, JSON.stringify(hosts))
      this.dragOverIndex = null
    },
    onDragEnd() {
      this.dragIndex = null
      this.dragOverIndex = null
    },
    // Rename
    startRename(server) {
      this.editingHostname = server.hostname
      this.editingName = server.display_name
      this.$nextTick(() => {
        const inputs = this.$refs.renameInput
        if (inputs) {
          const input = Array.isArray(inputs) ? inputs[inputs.length - 1] : inputs
          input.focus()
          input.select()
        }
      })
    },
    cancelRename() {
      this.editingHostname = null
      this.editingName = ''
    },
    async saveRename(hostname) {
      const newName = this.editingName.trim()
      if (!newName || newName === hostname) { this.cancelRename(); return }
      try {
        const res = await axios.post('/api/rename', { hostname, display_name: newName })
        if (res.data.code === 0) {
          const server = this.servers.find(s => s.hostname === hostname)
          if (server) server.display_name = newName
        }
      } catch (e) {
        console.error('重命名失败:', e)
      }
      this.cancelRename()
    },
    async deleteServer(server) {
      const name = server.display_name || server.hostname
      if (!confirm('确定要删除 "' + name + '" 吗？')) return
      try {
        await axios.delete('/api/server/' + encodeURIComponent(server.hostname))
        this.servers = this.servers.filter(s => s.hostname !== server.hostname)
      } catch (e) {
        console.error('删除失败:', e)
      }
    },
  },
}
</script>

<style scoped>
.dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0e27 0%, #0d1137 50%, #0f1433 100%);
  padding: 0 20px 20px;
}
.header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 0;
  border-bottom: 1px solid rgba(0, 150, 255, 0.15);
  margin-bottom: 20px; flex-wrap: wrap; gap: 15px;
}
.header-left { display: flex; align-items: baseline; gap: 15px; }
.title {
  font-size: 28px; font-weight: 700;
  background: linear-gradient(90deg, #00d4ff, #0096ff);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  background-clip: text; letter-spacing: 2px;
}
.subtitle { color: #5a6a8c; font-size: 14px; letter-spacing: 1px; }
.header-right { display: flex; align-items: center; gap: 25px; flex-wrap: wrap; }
.stat-item { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.stat-label { font-size: 12px; color: #5a6a8c; }
.stat-value { font-size: 24px; font-weight: 700; font-family: 'Consolas', 'Monaco', monospace; }
.stat-value.total { color: #00d4ff; }
.stat-value.online { color: #00e676; }
.stat-value.offline { color: #ff5252; }
.header-time { text-align: right; padding-left: 20px; border-left: 1px solid rgba(0, 150, 255, 0.15); }
.time-clock { font-size: 28px; font-weight: 700; font-family: 'Consolas', 'Monaco', monospace; color: #00d4ff; }
.time-date { font-size: 13px; color: #5a6a8c; margin-top: 2px; }

/* 工具栏 */
.toolbar {
  display: flex; align-items: center; gap: 15px; margin-bottom: 20px; flex-wrap: wrap;
}
.search-input {
  flex: 1; max-width: 360px; padding: 8px 14px;
  background: rgba(15, 25, 60, 0.8); border: 1px solid rgba(0, 150, 255, 0.15);
  border-radius: 8px; color: #e0e6ed; font-size: 14px; outline: none;
  transition: border-color 0.3s;
}
.search-input::placeholder { color: #3a4a6c; }
.search-input:focus { border-color: rgba(0, 150, 255, 0.4); }
.search-count { font-size: 13px; color: #5a6a8c; }

.column-switcher { display: flex; align-items: center; gap: 4px; }
.col-label { font-size: 12px; color: #5a6a8c; margin-right: 4px; }
.col-btn {
  background: rgba(15, 25, 60, 0.6); border: 1px solid rgba(0, 150, 255, 0.15);
  color: #5a6a8c; font-size: 12px; padding: 4px 10px; border-radius: 4px;
  cursor: pointer; transition: all 0.2s;
}
.col-btn:hover { border-color: rgba(0, 150, 255, 0.3); color: #8a9ab8; }
.col-btn.active { background: rgba(0, 150, 255, 0.15); border-color: #0096ff; color: #00d4ff; }

/* 空状态 */
.empty-state { grid-column: 1 / -1; text-align: center; padding: 100px 20px; color: #5a6a8c; }
.empty-icon { font-size: 60px; margin-bottom: 20px; }
.empty-state p { font-size: 18px; margin-bottom: 10px; }
.empty-hint { font-size: 14px !important; color: #3a4a6c; }

/* 服务器网格 */
.server-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(380px, 1fr)); gap: 20px; }

/* 服务器卡片 */
.server-card {
  background: linear-gradient(145deg, rgba(15, 25, 60, 0.9), rgba(10, 18, 45, 0.95));
  border: 1px solid rgba(0, 150, 255, 0.1); border-radius: 12px; padding: 20px;
  transition: all 0.3s ease; position: relative; overflow: hidden; cursor: grab;
  user-select: none;
}
.server-card:active { cursor: grabbing; }
.server-card.dragging { opacity: 0.4; transform: scale(0.95); }
.server-card.drag-over { border-color: #00d4ff; box-shadow: 0 0 20px rgba(0, 150, 255, 0.3); }
.server-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #0096ff, #00d4ff); opacity: 0; transition: opacity 0.3s;
}
.server-card:hover::before { opacity: 1; }
.server-card:hover {
  border-color: rgba(0, 150, 255, 0.3); transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 100, 200, 0.15);
}
.server-card.offline { opacity: 0.6; border-color: rgba(255, 80, 80, 0.15); }
.server-card.offline::before { background: linear-gradient(90deg, #ff5252, #ff8a80); opacity: 0.5; }

/* 卡片头部 */
.card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 18px; }
.server-name-row { display: flex; align-items: center; gap: 6px; }
.server-name {
  font-size: 18px; font-weight: 700; color: #e0e6ed; max-width: 200px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  cursor: pointer; padding: 2px 4px; border-radius: 4px; transition: background 0.2s;
}
.server-name:hover { background: rgba(0, 150, 255, 0.08); }
.edit-btn, .delete-btn {
  background: none; border: none; color: #3a4a6c; cursor: pointer;
  font-size: 14px; padding: 2px 4px; border-radius: 4px; transition: all 0.2s; flex-shrink: 0;
}
.edit-btn:hover { color: #00d4ff; background: rgba(0, 150, 255, 0.1); }
.delete-btn { padding: 2px 6px; margin-left: 4px; }
.delete-btn:hover { color: #ff5252; background: rgba(255, 82, 82, 0.1); }
.card-actions { display: flex; align-items: center; gap: 6px; }
.rename-input {
  font-size: 16px; font-weight: 700; color: #e0e6ed;
  background: rgba(0, 150, 255, 0.1); border: 1px solid #00d4ff;
  border-radius: 4px; padding: 2px 8px; width: 180px; outline: none;
}
.server-hostname { font-size: 11px; color: #3a4a6c; font-family: 'Consolas', 'Monaco', monospace; }
.server-ip { font-size: 12px; color: #5a6a8c; margin-top: 2px; font-family: 'Consolas', 'Monaco', monospace; }

.status-badge {
  display: flex; align-items: center; gap: 6px; padding: 4px 12px;
  border-radius: 20px; font-size: 12px; font-weight: 600;
}
.status-badge.online { background: rgba(0, 230, 118, 0.1); color: #00e676; border: 1px solid rgba(0, 230, 118, 0.2); }
.status-badge.offline { background: rgba(255, 82, 82, 0.1); color: #ff5252; border: 1px solid rgba(255, 82, 82, 0.2); }
.status-dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; animation: pulse 2s infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }

/* 指标行 */
.metric-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.metric-label { width: 42px; font-size: 13px; color: #8a9ab8; flex-shrink: 0; }
.progress-bar { flex: 1; height: 8px; background: rgba(255, 255, 255, 0.05); border-radius: 4px; overflow: hidden; }
.progress-fill { height: 100%; border-radius: 4px; transition: width 0.5s ease; min-width: 2px; }
.progress-fill.cpu { background: linear-gradient(90deg, #0096ff, #00d4ff); }
.progress-fill.memory { background: linear-gradient(90deg, #7c4dff, #b388ff); }
.progress-fill.disk { background: linear-gradient(90deg, #00c853, #69f0ae); }
.metric-value {
  font-size: 12px; font-family: 'Consolas', 'Monaco', monospace;
  color: #b0bec5; white-space: nowrap; min-width: 100px; text-align: right;
}
.metric-value.warning { color: #ffab40; }
.metric-value.danger { color: #ff5252; }

/* 网络 */
.network-section { margin-top: 15px; padding-top: 15px; border-top: 1px solid rgba(255, 255, 255, 0.05); }
.section-title { font-size: 14px; color: #8a9ab8; margin-bottom: 10px; font-weight: 600; }
.network-row { display: flex; gap: 20px; margin-bottom: 8px; }
.network-item { display: flex; align-items: center; gap: 6px; font-size: 16px; font-family: 'Consolas', 'Monaco', monospace; font-weight: 600; }
.network-item .arrow { font-size: 14px; }
.network-item.upload { color: #ffab40; }
.network-item.download { color: #00d4ff; }
.network-total { display: flex; gap: 15px; font-size: 11px; color: #4a5a7c; font-family: 'Consolas', 'Monaco', monospace; }

/* 卡片底部 */
.card-footer { margin-top: 12px; padding-top: 10px; border-top: 1px solid rgba(255, 255, 255, 0.05); display: grid; grid-template-columns: 1fr 1fr; gap: 4px 10px; }
.footer-item { font-size: 11px; color: #4a5a7c; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.footer-label { color: #5a6a8c; }

/* 刷新栏 */
.refresh-bar { text-align: center; padding: 15px; font-size: 12px; color: #3a4a6c; display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 10px; }
.refresh-dot { width: 6px; height: 6px; border-radius: 50%; background: #00d4ff; animation: pulse 2s infinite; }

/* 响应式 */
@media (max-width: 768px) {
  .server-grid { grid-template-columns: 1fr; }
  .title { font-size: 22px; }
  .header { flex-direction: column; align-items: flex-start; }
  .header-time { border-left: none; padding-left: 0; }
}
</style>
